import React from 'react';
import { ErrorOutline as ErrorIcon,School as LearningIcon } from '@mui/icons-material';
import { Box, Card, CardContent, Divider, Link,Stack, Typography } from '@mui/material';

import type { LearningRule } from '../types';

interface LearningRuleCardProps {
  rule: LearningRule;
}

export const LearningRuleCard: React.FC<LearningRuleCardProps> = ({ rule }) => {
  return (
    <Card
      sx={{
        width: 300,
        minWidth: 300,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 1,
          borderColor: 'borderColors.brand',
        },
      }}
    >
      {/* Header Section with Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <LearningIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              color: 'textColors.darkText',
              mb: 0.25,
            }}
          >
            {rule.title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}>
            Learning Rule
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        <Stack spacing={1}>
          {/* Teaching Summary */}
          <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
            {rule.teaching_summary}
          </Typography>

          {/* Recommended Actions */}
          {rule.recommended_actions && rule.recommended_actions.trim() !== '' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: 0.75,
                borderRadius: 1,
                bgcolor: 'backgroundColors.tertiary',
                border: '1px solid',
                borderColor: 'borderColors.lightprimary',
              }}
            >
              <ErrorIcon
                sx={{
                  fontSize: '1rem',
                  color: 'primary.main',
                  flexShrink: 0,
                  mt: 0.125,
                }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText', flex: 1 }}>
                {rule.recommended_actions}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>

      {/* Footer with Source Information */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'backgroundColors.secondary',
          borderTop: '1px solid',
          borderColor: 'borderColors.primary',
        }}
      >
        <Typography variant="caption" sx={{ color: 'textColors.lightText', fontSize: '0.6875rem', fontStyle: 'italic' }}>
          Source:{' '}
          {rule.source_link ? (
            <Link href={rule.source_link} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit' }}>
              <strong>{rule.source_document}</strong>
            </Link>
          ) : (
            <strong style={{ color: 'inherit' }}>{rule.source_document}</strong>
          )}
        </Typography>
      </Box>
    </Card>
  );
};

